// Only opaque IDs, a content digest and version metadata may persist here.
// Message bodies/parameters stay in the authenticated server draft, never here.
const prefix = 'tutory.inbox.requests.v1:';
const problem = code => Object.assign(new Error('Inbox recovery unavailable'), { code });

function ownerPrefix(userId) {
  if (!Number.isSafeInteger(userId) || userId < 1) throw problem('recovery_storage_unavailable');
  return `${prefix}${userId}:`;
}

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
  return value;
}

export async function requestDigest(thread, endpoint, payload) {
  const bytes = new TextEncoder().encode(JSON.stringify(canonical({ thread, endpoint, payload })));
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function readRequests(userId, thread) {
  try {
    const start = ownerPrefix(userId), rows = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key?.startsWith(start)) continue;
      const row = JSON.parse(localStorage.getItem(key));
      if (!row || typeof row.thread !== 'string' || typeof row.key !== 'string' || key !== start + row.key
          || !/^[a-f0-9]{64}$/.test(row.digest) || !['messages', 'notes', 'templates'].includes(row.endpoint)
          || !Number.isSafeInteger(row.draftVersion) || row.draftVersion < 0
          || !['pending', 'rejected'].includes(row.state) || !Number.isFinite(row.createdAt)) {
        throw problem('recovery_storage_unavailable');
      }
      if (!thread || row.thread === thread) rows.push(row);
    }
    return rows.sort((left, right) => left.createdAt - right.createdAt);
  } catch (error) { throw problem(typeof error.code === 'string' ? error.code : 'recovery_storage_unavailable'); }
}

export function rememberRequest(userId, row) {
  try {
    const key = ownerPrefix(userId) + row.key;
    if (localStorage.getItem(key) === null && readRequests(userId).length >= 100) throw problem('recovery_storage_full');
    const safe = { key: row.key, thread: row.thread, digest: row.digest, endpoint: row.endpoint,
      draftVersion: row.draftVersion, state: row.state, createdAt: row.createdAt };
    const encoded = JSON.stringify(safe);
    localStorage.setItem(key, encoded);
    if (localStorage.getItem(key) !== encoded) throw problem('recovery_storage_unavailable');
    return safe;
  } catch (error) { throw problem(typeof error.code === 'string' ? error.code : 'recovery_storage_unavailable'); }
}

export function forgetRequest(userId, key) {
  try { localStorage.removeItem(ownerPrefix(userId) + key); }
  catch { throw problem('recovery_storage_unavailable'); }
}

// The server owns prepared commands. Local storage is mandatory only when
// creating a NEW intention; it must not prevent recovery of an existing one.
export function createRecoverySession(userId, onStorageProblem) {
  const memory = new Map(), settled = new Set();
  const warn = error => onStorageProblem(error.code || 'recovery_storage_unavailable');
  return {
    read(thread) {
      let stored = [];
      try { stored = readRequests(userId); } catch (error) { warn(error); }
      const rows = new Map(stored.map(row => [row.key, row]));
      for (const [key, row] of memory) rows.set(key, row);
      return [...rows.values()].filter(row => !settled.has(row.key) && (!thread || row.thread === thread));
    },
    remember(row, { serverOwned = false } = {}) {
      let saved = row;
      try { saved = rememberRequest(userId, row); onStorageProblem(''); }
      catch (error) { warn(error); if (!serverOwned) throw error; }
      memory.set(saved.key, saved);
      return saved;
    },
    track(row) { memory.set(row.key, row); },
    forget(key) {
      memory.delete(key);
      try { forgetRequest(userId, key); settled.delete(key); }
      catch (error) { settled.add(key); warn(error); }
    },
  };
}

export function clearInboxRecovery() {
  // Explicit logout must not retain another staff member's recovery metadata.
  for (let index = localStorage.length - 1; index >= 0; index--) {
    const key = localStorage.key(index);
    if (key?.startsWith(prefix)) localStorage.removeItem(key);
  }
}
